// Call plugin.
import { callActions } from '../../call/interfaceNew/actions'
import { getOptions } from '../../call/interfaceNew/selectors'

// Helpers
import { logManager } from '../../logs'
import runPipeline from '../sdp/pipeline'
import { getTrackDscpMapping } from '../utils/dscp'
import BasicError, { callCodes } from '../../errors'

// WebRTC operations.
import * as mediaOps from './media'

// Libraries.
import { call, put, select } from 'redux-saga/effects'

/**
 * Performs the WebRTC portion necessary for establishing outgoing calls
 *
 * @method setupCall
 * @param  {Object} deps
 * @param  {Object} deps.webRTC      The WebRTC stack.
 * @param  {Object} mediaConstraints Video and audio media constraints
 * @param  {boolean} mediaConstraints.audio Whether to enable audio or not
 * @param  {boolean} mediaConstraints.video Whether to enable video or not
 * @param  {Object} sessionOptions
 * @param  {Object} sessionOptions.callId The id of the call
 * @param  {Object} sessionOptions.sdpSemantics Semantics for Real Time Communication configurations
 * @param  {Object} sessionOptions.turnInfo Contains information required for setting up ICE servers
 * @param  {string} sessionOptions.trickleIceMode What mode to be used for trickle ICE
 * @param  {Object} [sessionOptions.bandwidth] Contains configuration details for setting bandwidth
 * @return {Object} Object
 * @return {string} Object.offerSdp Session Description Protocol for a call offer
 * @return {string} Object.sessionId session identifier
 * @return {Array}  Object.mediaIds call identifiers
 */
export function * setupCall (deps, mediaConstraints, sessionOptions) {
  const { webRTC } = deps

  const log = logManager.getLogger('CALL', sessionOptions.callId)
  log.info('Setting up local WebRTC portions of call.')

  const {
    defaultPeerConfig,
    turnInfo,
    trickleIceMode,
    bandwidth,
    dscpControls,
    callId,
    removeBundling
  } = sessionOptions

  const { medias, error } = yield call(mediaOps.createLocal, webRTC, mediaConstraints)

  if (error) {
    log.debug('Failed to get media requested for the call.')
    return { error }
  }

  let session
  try {
    // Create a webRTC session to represent this call.
    session = yield call([webRTC.sessionManager, 'create'], {
      peer: {
        rtcConfig: {
          ...defaultPeerConfig,
          iceServers: turnInfo.servers
        },
        trickleIceMode,
        removeBundling
      }
    })
    log.debug('Created WebRTC Session for Call.', { webrtcSessionId: session.id })
  } catch (error) {
    log.debug('Failed to create WebRTC Session for Call.', error.message)
    return {
      error: new BasicError({
        message: error.message,
        code: callCodes.GENERIC_ERROR
      })
    }
  }

  // Trigger a new action specifying that the session has been created
  yield put(
    callActions.sessionCreated(callId, {
      webrtcSessionId: session.id
    })
  )

  // Add the tracks to the session.
  let screenTracks = []
  let audioTracks = []
  let videoTracks = []
  let allTracks = []

  for (const eachMedia of medias) {
    const tracks = yield call([eachMedia.media, 'getTracks'])
    if (eachMedia.type === 'screen') {
      screenTracks = [...screenTracks, ...tracks]
    } else if (eachMedia.type === 'audio') {
      audioTracks = [...audioTracks, ...tracks]
    } else if (eachMedia.type === 'video') {
      videoTracks = [...videoTracks, ...tracks]
    }
    allTracks = [...allTracks, ...tracks]
  }

  const dscpTrackMapping = getTrackDscpMapping(audioTracks, videoTracks, screenTracks, dscpControls)

  yield call([session, 'addTracks'], allTracks, dscpTrackMapping)

  /*
   * Create the local SDP offer, run it through any provided SDP handlers,
   *    then set it as the Session's local description.
   */
  let offer, callConfigOptions
  try {
    offer = yield call([session, 'createOffer'])

    callConfigOptions = yield select(getOptions)

    // Run the SDP through the Pipeline before we set it locally.
    //    This is the "pre set local" stage.
    offer.sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
      callId,
      type: offer.type,
      step: 'set',
      endpoint: 'local',
      bandwidth
    })
    offer = yield call([session, 'setLocalDescription'], offer)
  } catch (e) {
    return {
      error: new BasicError({
        message: e.message,
        code: callCodes.GENERIC_ERROR
      }),
      offerSdp: null,
      sessionId: null,
      mediaIds: null
    }
  }
  // Run the SDP through the Pipeline again before we send it to the remote side.
  //    This is the "pre send local" stage.
  // Assign it to a new variable because some browsers enforce the read-only
  //    properties of a RTCSessionDescription object. The object from
  //    setLocalDescription is enforced read-only, but the `offer` before that
  //    is not enforced.
  const newSdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
    callId,
    type: offer.type,
    step: 'send',
    endpoint: 'local',
    bandwidth
  })

  log.info('Finished setting up local WebRTC portions of call.')
  return {
    error: false,
    offerSdp: newSdp,
    sessionId: session.id,
    mediaIds: medias.map(media => media.media.id)
  }
}

/**
 * Prepares the WebRTC portions of setting up an incoming call.
 *
 * @method setupIncomingCall
 * @param  {Object} deps
 * @param  {Object} deps.webRTC      The WebRTC stack.
 * @param  {Object} sessionOptions
 * @param  {string} sessionOptions.callId the local call id.
 * @param  {Object} sessionOptions.sdpSemantics semantics for the SDP to use (`'unified-plan'` or `'plan-b'`). It implies video and audio constraints.
 * @param  {Object} sessionOptions.turnInfo TURN information, contains server info.
 * @param  {string} sessionOptions.trickleIceMode the mode to enable for Trickle ICE.
 * @param  {Object} sessionOptions.offer an offer containing an SDP.
 * @return {Session} WebRTC Session for the call.
 */
export function * setupIncomingCall (deps, sessionOptions) {
  const { webRTC } = deps
  const { defaultPeerConfig, turnInfo, trickleIceMode, callId, removeBundling } = sessionOptions
  const offer = sessionOptions.offer

  const log = logManager.getLogger('CALL', callId)
  log.info('Setting up remote WebRTC portions of call.')

  let session
  try {
    session = yield call([webRTC.sessionManager, 'create'], {
      peer: {
        rtcConfig: {
          ...defaultPeerConfig,
          iceServers: turnInfo.servers
        },
        trickleIceMode,
        removeBundling
      }
    })
  } catch (error) {
    log.debug('Failed to create WebRTC Session for incoming Call.', error.message)
    return {
      error: new BasicError({
        message: error.message,
        code: callCodes.GENERIC_ERROR
      })
    }
  }

  // Trigger a new action specifying that the session has been created
  yield put(
    callActions.sessionCreated(callId, {
      webrtcSessionId: session.id
    })
  )

  /*
   * Run the remote SDP offer through any SDP handlers provided, then set it
   *    as the Session's remote description.
   * This is the "pre set remote" stage.
   */
  const callConfigOptions = yield select(getOptions)
  offer.sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
    callId,
    type: offer.type,
    step: 'set',
    endpoint: 'remote'
  })

  try {
    yield call([session, 'processOffer'], {
      type: 'offer',
      sdp: offer.sdp
    })
  } catch (error) {
    return {
      sessionId: null,
      error
    }
  }

  log.info('Finished setting up remote WebRTC portions of call.')
  return { session }
}

/**
 * Prepares the WebRTC portions of answering a call
 *
 * @method answerWebrtcSession
 * @param  {Object} deps
 * @param  {Object} deps.webRTC      The WebRTC stack.
 * @param  {Object} mediaConstraints Video and audio media constraints
 * @param  {boolean} mediaConstraints.audio Whether to enable audio or not
 * @param  {boolean} mediaConstraints.video Whether to enable video or not
 * @param  {Object} sessionOptions
 * @param  {string} sessionOptions.sessionId the local webrtc session id
 * @param  {string} sessionOptions.callId the local call id
 * @param  {Object} [sessionOptions.bandwidth] Contains configuration details for setting bandwidth
 * @return {Object} Object
 * @return {string} Object.answerSDP Session Description Protocol for answer
 * @return {string} Object.mediaId an identifier for media
 */
export function * answerWebrtcSession (deps, mediaConstraints, sessionOptions) {
  const { webRTC } = deps
  const { sessionId, bandwidth, dscpControls, callId } = sessionOptions

  const log = logManager.getLogger('CALL', callId)
  log.info('Setting up local WebRTC portions of call.')

  // Get the webRTC session that represents this call.
  let session
  try {
    session = yield call([webRTC.sessionManager, 'get'], sessionId)
  } catch (error) {
    /*
     * A WebRTC operation should only throw in Proxy mode when the channel times-out
     *    the operation. Check the first "get" in the saga to catch this problem.
     */
    log.debug(error.message)
    return { error }
  }

  if (!session) {
    log.error(`Error: webRTC session ${sessionId} not found.`)
    return
  }

  const { medias, error } = yield call(mediaOps.createLocal, webRTC, mediaConstraints)

  if (error) {
    log.debug('Failed to get media requested for the call.')
    return { error }
  }

  let screenTracks = []
  let audioTracks = []
  let videoTracks = []
  let allTracks = []

  // Add the tracks to the session.
  for (const eachMedia of medias) {
    const tracks = yield call([eachMedia.media, 'getTracks'])
    if (eachMedia.type === 'screen') {
      screenTracks = [...screenTracks, ...tracks]
    } else if (eachMedia.type === 'audio') {
      audioTracks = [...audioTracks, ...tracks]
    } else if (eachMedia.type === 'video') {
      videoTracks = [...videoTracks, ...tracks]
    }
    allTracks = [...allTracks, ...tracks]
  }

  const dscpTrackMapping = getTrackDscpMapping(audioTracks, videoTracks, screenTracks, dscpControls)

  yield call([session, 'addTracks'], allTracks, dscpTrackMapping)

  /*
   * Create the local SDP answer, run it through any provided SDP handlers,
   *    then set it as the Session's local description.
   */
  let answer, callConfigOptions
  try {
    answer = yield call([session, 'createAnswer'])
    callConfigOptions = yield select(getOptions)
    // This is the "pre set local" stage.
    answer.sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, answer.sdp, {
      callId,
      type: answer.type,
      step: 'set',
      endpoint: 'local',
      bandwidth
    })
    answer = yield call([session, 'setLocalDescription'], answer)
  } catch (e) {
    return {
      error: new BasicError({
        message: e.message,
        code: callCodes.GENERIC_ERROR
      }),
      answerSDP: null,
      mediaIds: null
    }
  }

  // Run the SDP through the Pipeline again before we send it to the remote side.
  //    This is the "pre send local" stage.
  // Assign it to a new variable because some browsers enforce the read-only
  //    properties of a RTCSessionDescription object. The object from
  //    setLocalDescription is enforced read-only, but the `offer` before that
  //    is not enforced.
  const newSdp = yield call(runPipeline, callConfigOptions.sdpHandlers, answer.sdp, {
    callId,
    type: answer.type,
    step: 'send',
    endpoint: 'local',
    bandwidth
  })

  log.info('Finished setting up local WebRTC portions of call.')

  return {
    error: false,
    answerSDP: newSdp,
    mediaIds: medias.map(media => media.media.id)
  }
}
